import React, { useEffect, useRef, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { MapPin } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
// @ts-ignore
import polyline from 'polyline';

// Map styles for different themes
const tileLayerUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

// Add custom marker styles with fixed positioning
const mapStyles = `
  .map-container {
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 0;
  }

  .custom-marker {
    position: relative;
    width: 40px !important;
    height: 40px !important;
  }

  .marker-pin {
    width: 18px;
    height: 18px;
    position: absolute;
    left: 11px;
    top: 11px;
    background: #3b82f6;
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 0 0 2px #3b82f6;
    z-index: 1;
  }

  .marker-pulse {
    width: 40px;
    height: 40px;
    background: rgba(59, 130, 246, 0.15);
    border-radius: 50%;
    position: absolute;
    left: 0;
    top: 0;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% {
      transform: scale(0.5);
      opacity: 1;
    }
    100% {
      transform: scale(2);
      opacity: 0;
    }
  }

  .leaflet-container {
    width: 100% !important;
    height: 100% !important;
    border-radius: 0.75rem;
    background: #f8fafc;
    overflow: hidden;
    z-index: 0;
  }

  .leaflet-control-zoom {
    margin: 1rem !important;
    border-radius: 0.5rem !important;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
    z-index: 1;
  }

  .leaflet-control-zoom a {
    width: 32px !important;
    height: 32px !important;
    line-height: 30px !important;
    color: #1e293b !important;
    background: white !important;
    border: 1px solid #e2e8f0 !important;
    font-size: 16px !important;
  }

  .leaflet-control-zoom a:first-child {
    border-bottom: 1px solid #e2e8f0 !important;
  }

  .leaflet-control-zoom a:hover {
    background: #f8fafc !important;
    color: #3b82f6 !important;
  }

  .leaflet-tile-container img {
    filter: contrast(0.95) brightness(1.05);
  }

  .leaflet-control-attribution {
    background: rgba(255, 255, 255, 0.9) !important;
    padding: 0 8px !important;
    border-radius: 4px !important;
    margin: 4px !important;
    font-size: 10px !important;
  }

  .address-overlay {
    position: absolute;
    top: 1rem;
    left: 1rem;
    background: white;
    padding: 0.75rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    max-width: calc(100% - 2rem);
  }

  .address-icon {
    color: #3b82f6;
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }

  .address-text {
    min-width: 0;
    flex: 1;
  }
`;

interface Marker {
  position: { lat: number; lng: number } | [number, number];
  popupText?: string;
  iconHtml?: string;
}

interface SimpleMapProps {
  center: { lat: number; lng: number } | [number, number];
  zoom?: number;
  className?: string;
  onError?: () => void;
  showLocationIndicator?: boolean;
  mapStyle?: 'light' | 'dark' | 'default';
  address?: {
    street?: string;
    city?: string;
    postcode?: string;
    venue?: string;
  };
  markers?: Marker[];
  height?: string | number;
}

export function SimpleMap({
  center,
  zoom = 15,
  className = '',
  onError,
  showLocationIndicator = false,
  mapStyle = 'default',
  address,
  markers = [],
  height = 300
}: SimpleMapProps) {
  const mapRef = useRef<L.Map | null>(null);
  const markerRefs = useRef<L.Marker[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [userLocation, setUserLocation] = React.useState<L.LatLngTuple | null>(null);
  const [route, setRoute] = React.useState<L.LatLngTuple[] | null>(null);
  const routeLayerRef = useRef<L.Polyline | null>(null);
  const coordinates = useMemo<L.LatLngTuple | null>(() => {
    if (Array.isArray(center)) {
      if (center.length === 2 && !isNaN(center[0]) && !isNaN(center[1])) return center as L.LatLngTuple;
      return null;
    }
    if (center && typeof center.lat === 'number' && typeof center.lng === 'number' && !isNaN(center.lat) && !isNaN(center.lng)) {
      return [center.lat, center.lng] as L.LatLngTuple;
    }
    return null;
  }, [center]);

  // Add custom styles to head
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = mapStyles;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Tile layer selection for dark mode
  const tileLayer = useMemo(() => {
    if (mapStyle === 'dark') {
      return 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
    }
    return tileLayerUrl;
  }, [mapStyle]);

  // Initialize map only once
  useEffect(() => {
    if (!containerRef.current || mapRef.current || !coordinates) return;
    setLoading(true);
    try {
      const map = L.map(containerRef.current, {
        zoomControl: true,
        scrollWheelZoom: true,
        dragging: true,
        doubleClickZoom: true,
        boxZoom: true,
        keyboard: true,
        attributionControl: true
      }).setView(coordinates, zoom);
      mapRef.current = map;
      L.tileLayer(tileLayer, {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
        minZoom: 3
      }).addTo(map);
      L.control.scale({
        maxWidth: 100,
        metric: true,
        imperial: false,
        position: 'bottomright'
      }).addTo(map);
      setLoading(false);
      return () => {
        map.remove();
        mapRef.current = null;
        markerRefs.current = [];
      };
    } catch (err) {
      setLoading(false);
      setError('Unable to load map');
      onError?.();
    }
  }, [containerRef, coordinates, tileLayer, zoom, onError]); // Only run on mount or tileLayer/zoom change

  // Manage markers (clear and re-add)
  useEffect(() => {
    if (!mapRef.current) return;
    // Remove old markers
    markerRefs.current.forEach(marker => marker.remove());
    markerRefs.current = [];

    // Classic Leaflet marker icon with explicit correct anchor and size
    const classicIcon = L.icon({
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });

    // Add event location marker (main marker) - always at event location
    if (coordinates) {
      const eventMarker = L.marker(coordinates, {
        icon: classicIcon
      }).addTo(mapRef.current);
      if (address) {
        const addressText = [address.venue, address.street, address.city, address.postcode].filter(Boolean).join(', ');
        eventMarker.bindPopup(addressText);
      }
      markerRefs.current.push(eventMarker);
    }

    // Add additional markers
    markers.forEach(({ position, popupText }) => {
      let pos: L.LatLngTuple | null = null;
      if (Array.isArray(position)) {
        pos = position as L.LatLngTuple;
      } else if (position && typeof position.lat === 'number' && typeof position.lng === 'number') {
        pos = [position.lat, position.lng];
      }
      if (pos) {
        const marker = L.marker(pos, { icon: classicIcon }).addTo(mapRef.current!);
        if (popupText) marker.bindPopup(popupText);
        markerRefs.current.push(marker);
      }
    });

    // Add user location marker with the classic Leaflet icon - always at userLocation
    if (userLocation) {
      const userMarker = L.marker(userLocation, {
        icon: classicIcon
      }).addTo(mapRef.current!);
      userMarker.bindPopup('You are here');
      markerRefs.current.push(userMarker);
    }
  }, [coordinates, markers, userLocation, address]);

  // Ensure map view only updates on event location/zoom change, not user location
  useEffect(() => {
    if (mapRef.current && coordinates) {
      mapRef.current.setView(coordinates, zoom, { animate: true });
    }
  }, [coordinates, zoom]); // userLocation is intentionally NOT included here

  // Update tile layer if mapStyle changes
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.eachLayer((layer) => {
      if ((layer as any).options && (layer as any).options.attribution === '© OpenStreetMap contributors') {
        mapRef.current?.removeLayer(layer);
      }
    });
    L.tileLayer(tileLayer, {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
      minZoom: 3
    }).addTo(mapRef.current);
  }, [tileLayer]);

  // Fetch route from userLocation to event location
  useEffect(() => {
    if (!userLocation || !coordinates) {
      setRoute(null);
      if (routeLayerRef.current) {
        routeLayerRef.current.remove();
        routeLayerRef.current = null;
      }
      return;
    }
    // Fetch route from OSRM public API
    const fetchRoute = async () => {
      try {
        const url = `https://router.project-osrm.org/route/v1/foot/${userLocation[1]},${userLocation[0]};${coordinates[1]},${coordinates[0]}?overview=full&geometries=polyline`;
        const res = await fetch(url);
        const data = await res.json();
        if (data.routes && data.routes[0] && data.routes[0].geometry) {
          const decoded = polyline.decode(data.routes[0].geometry);
          // polyline.decode returns [lat, lng] pairs
          setRoute(decoded.map((pair: [number, number]) => [pair[0], pair[1]] as L.LatLngTuple));
        } else {
          setRoute(null);
        }
      } catch (e) {
        setRoute(null);
      }
    };
    fetchRoute();
  }, [userLocation, coordinates]);

  // Draw route polyline on map
  useEffect(() => {
    if (!mapRef.current) return;
    if (routeLayerRef.current) {
      routeLayerRef.current.remove();
      routeLayerRef.current = null;
    }
    if (route && route.length > 1) {
      const poly = L.polyline(route, {
        color: '#3b82f6',
        weight: 5,
        opacity: 0.85,
        dashArray: '8, 8',
        lineJoin: 'round',
        className: 'event-route-polyline'
      }).addTo(mapRef.current!);
      routeLayerRef.current = poly;
      // Fit bounds to both points and route
      const bounds = L.latLngBounds([...route, userLocation!, coordinates!]);
      mapRef.current.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [route, userLocation, coordinates]);

  // Geolocation handler
  const handleLocateMe = () => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported');
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation([pos.coords.latitude, pos.coords.longitude]);
        setLoading(false);
      },
      (err) => {
        setError('Unable to get your location');
        setLoading(false);
      }
    );
  };

  if (error || !coordinates) {
    return (
      <div className={cn('flex items-center justify-center bg-slate-50 text-gray-500', className)} style={{height: height, width: '100%'}} role="region" aria-label="Map unavailable">
        {error || 'Map currently unavailable'}
      </div>
    );
  }

  return (
    <div className={cn('relative w-full h-full', className)} style={{ minHeight: height }}>
      {/* Address Overlay */}
      {address && (
        <div className="address-overlay" aria-label="Map address">
          <MapPin className="address-icon" aria-hidden="true" />
          <span className="address-text truncate">
            {[address.venue, address.street, address.city, address.postcode].filter(Boolean).join(', ')}
          </span>
        </div>
      )}
      {/* Geolocation Button */}
      {showLocationIndicator && (
        <button
          type="button"
          aria-label="Locate me"
          className="absolute z-10 right-4 top-4 bg-white border border-slate-200 rounded-full shadow p-2 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
          style={{ transition: 'background 0.2s' }}
          onClick={handleLocateMe}
          tabIndex={0}
        >
          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/></svg>
        </button>
      )}
      {/* Map Container */}
      <div
        ref={containerRef}
        className={cn('w-full h-full', 'map-container')}
        style={{ minHeight: height }}
        aria-label="Map"
        tabIndex={0}
      />
      {/* Loading Spinner */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-20">
          <span className="text-blue-500 animate-spin" aria-label="Loading map">⏳</span>
        </div>
      )}
    </div>
  );
} 